import type { components } from '../api/schema.gen';
import type {
  Actor,
  CheckVerdict,
  AdBudget,
  AdPlatform,
  AutonomyKind,
  ChecklistEntry,
  Contact,
  Employee,
  EmployeeModule,
  EmployeeProfile,
  EmployeeRequest,
  EmployeeSummary,
  EventKind,
  Execution,
  Item,
  ItemAction,
  ItemEvent,
  ItemPayload,
  ItemStatus,
  ItemType,
  KindAutonomy,
  Metric,
  ModuleCategory,
  ModuleQuestion,
  PriceLine,
  QuestionKind,
  RequestStatus,
  Role,
  Session,
  SessionAction,
  WorkKind,
  Workspace,
  WorkspaceSummary,
} from './models';

// DTO → domain. Only the data layer calls these; screens never see generated types.

type S = components['schemas'];

const oneOf = <T extends string>(v: string, known: readonly T[], fallback: T): T =>
  (known as readonly string[]).includes(v) ? (v as T) : fallback;

const itemTypes = ['approval', 'screening', 'report', 'task'] as const;
const itemStatuses = [
  'pending',
  'changes_requested',
  'approved',
  'rejected',
  'completed',
  'failed',
  'withdrawn',
  'expired',
] as const;
const executions = ['none', 'manual', 'source'] as const;
const verdicts = ['ok', 'spam', 'injection', 'abuse', 'unclear'] as const;
const workKinds = [
  'request',
  'report',
  'task',
  'offer',
  'deliverable',
  'access_request',
  'reply',
  'message',
  'post',
  'group_post',
  'ad',
  'document',
  'other',
] as const;
const autonomyKinds = workKinds.filter((k): k is AutonomyKind => k !== 'task');
const isAutonomyKind = (k: string): k is AutonomyKind => (autonomyKinds as readonly string[]).includes(k);
const itemActions = ['approve', 'reject', 'request_changes', 'edit', 'hand_off', 'report_result'] as const;
const eventKinds = {
  'item.created': 'created',
  'item.revised': 'revised',
  'item.approved': 'approved',
  'item.rejected': 'rejected',
  'item.changes_requested': 'changes_requested',
  'item.completed': 'completed',
  'item.failed': 'failed',
  'item.withdrawn': 'withdrawn',
  'item.expired': 'expired',
  'item.handed_off': 'handed_off',
} as const satisfies Record<string, EventKind>;

const date = (s: string) => new Date(s);
const optDate = (s: string | null | undefined) => (s ? new Date(s) : undefined);
const opt = <T>(v: T | null | undefined) => v ?? undefined;

export function sessionFrom(me: S['Me']): Session {
  const role: Role = me.role === 'owner' || me.role === 'approver' ? me.role : 'viewer';
  return {
    userId: me.user.id,
    name: me.user.name,
    email: me.user.email,
    organizationName: me.organization.name,
    role,
    // Actions this version does not know are skipped, as for items.
    actions: new Set(me.actions.filter((a): a is SessionAction => a === ('request_employee' satisfies S['MeAction']))),
  };
}

export const workspaceFrom = (w: S['Workspace']): Workspace => ({
  id: w.id,
  slug: w.slug,
  name: w.name,
  description: w.description,
  color: w.color,
  openCount: w.open_count,
  employeeCount: w.employee_count,
});

export const workspaceSummaryFrom = (w: S['WorkspaceRef']): WorkspaceSummary => ({
  id: w.id,
  slug: w.slug,
  name: w.name,
  color: w.color,
});

export const employeeFrom = (e: S['Employee']): Employee => ({
  id: e.id,
  workspaceId: e.workspace_id,
  role: e.role,
  name: e.name,
  title: e.title,
  description: e.description,
  avatar: e.avatar,
  color: e.color,
  active: e.status === 'active',
  openCount: e.open_count,
  inProgressCount: e.in_progress_count,
  takesTasks: e.takes_tasks,
  // Kinds this version does not know are skipped: it has no words or icon for them.
  autonomy: e.autonomy.flatMap((a): KindAutonomy[] =>
    isAutonomyKind(a.kind) ? [{ kind: a.kind, automatic: a.automatic, allowed: a.allowed }] : [],
  ),
  // Actions this version does not know are skipped, as for items.
  canSetAutonomy: e.actions.includes('set_autonomy' satisfies S['EmployeeAction']),
  autonomyChangedAt: optDate(e.autonomy_changed_at),
  autonomyChangedBy: opt(e.autonomy_changed_by),
  profile: profileFrom(e.profile),
  canEditProfile: e.actions.includes('edit_profile' satisfies S['EmployeeAction']),
  lastActivityAt: optDate(e.last_activity_at),
});

export const profileFrom = (p: S['EmployeeProfile'] | undefined): EmployeeProfile => ({
  answers: Object.fromEntries(Object.entries(p?.answers ?? {}).filter(([, v]) => typeof v === 'string')),
  connections: (p?.connections ?? []).filter((c) => typeof c === 'string'),
  note: p?.note?.trim() ? p.note : undefined,
});

/** The profile as the API takes it. */
export const profileToDto = (p: EmployeeProfile): S['EmployeeProfile'] => ({
  answers: p.answers,
  connections: p.connections,
  note: p.note?.trim() ? p.note.trim() : null,
});

const categories = ['customers', 'marketing', 'sales', 'admin', 'tech'] as const;
const questionKinds = ['text', 'long', 'url', 'choice'] as const;
const requestStatuses = ['requested', 'in_progress', 'done', 'declined'] as const;
const isWorkKind = (k: string): k is WorkKind => (workKinds as readonly string[]).includes(k);

export const moduleFrom = (m: S['EmployeeModule']): EmployeeModule => ({
  role: m.role,
  name: m.name,
  title: m.title,
  summary: m.summary,
  description: m.description,
  avatar: m.avatar,
  color: m.color,
  category: oneOf<ModuleCategory>(m.category, categories, 'admin'),
  // Kinds this version does not know are skipped.
  kinds: m.kinds.filter(isWorkKind),
  automatable: m.automatable.filter(isAutonomyKind),
  skills: m.skills,
  connections: m.connections.map((c) => ({ key: c.key, label: c.label })),
  industries: m.industries,
  questions: m.questions.map(
    (q): ModuleQuestion => ({
      key: q.key,
      label: q.label,
      help: q.help?.trim() ? q.help : undefined,
      kind: oneOf<QuestionKind>(q.kind, questionKinds, 'text'),
      options: q.options ?? [],
      required: q.required,
    }),
  ),
  takesTasks: m.takes_tasks,
});

export const requestFrom = (r: S['EmployeeRequest']): EmployeeRequest => ({
  id: r.id,
  role: opt(r.role),
  title: r.title,
  workspaceId: opt(r.workspace_id),
  workspaceName: opt(r.workspace_name),
  workspaceDescription: opt(r.workspace_description),
  profile: profileFrom(r.profile),
  automaticKinds: r.automatic_kinds.filter(isAutonomyKind),
  status: oneOf<RequestStatus>(r.status, requestStatuses, 'unknown'),
  requestedBy: r.requested_by,
  employeeId: opt(r.employee_id),
  createdAt: date(r.created_at),
  updatedAt: date(r.updated_at),
});

export const employeeSummaryFrom = (e: S['EmployeeRef']): EmployeeSummary => ({
  id: e.id,
  role: e.role,
  name: e.name,
  title: e.title,
  avatar: e.avatar,
  color: e.color,
});

export const actorFrom = (a: S['Actor']): Actor => ({
  type: a.type === 'user' || a.type === 'api_key' ? a.type : 'system',
  name: a.name,
});

export function itemFrom(i: S['Item'], etag?: string): Item {
  return {
    id: i.id,
    workspace: workspaceSummaryFrom(i.workspace),
    employee: employeeSummaryFrom(i.employee),
    type: oneOf<ItemType>(i.type, itemTypes, 'unknown'),
    kind: oneOf<WorkKind>(i.kind, workKinds, 'other'),
    // Actions this version does not know are left out: the app cannot offer them anyway.
    actions: new Set(i.actions.filter((a): a is ItemAction => (itemActions as readonly string[]).includes(a))),
    outward: i.outward,
    schema: i.schema,
    status: oneOf<ItemStatus>(i.status, itemStatuses, 'unknown'),
    execution: oneOf<Execution>(i.execution, executions, 'unknown'),
    priority: i.priority === 'high' || i.priority === 'low' ? i.priority : 'normal',
    title: i.title,
    summary: opt(i.summary),
    revision: i.revision,
    payload: i.redacted || !i.payload ? { type: 'hidden' } : payloadFrom(i.schema, i.payload),
    externalRef: opt(i.external_ref),
    parentId: opt(i.parent_id),
    links: (i.links ?? []).map((l) => ({ label: l.label, url: l.url })),
    decision: i.decision
      ? {
          kind: i.decision.decision,
          automatic: i.decision.automatic,
          decidedBy: actorFrom(i.decision.decided_by),
          decidedAt: date(i.decision.decided_at),
          comment: opt(i.decision.comment),
        }
      : undefined,
    result: i.result
      ? {
          succeeded: i.result.outcome === 'completed',
          reportedBy: actorFrom(i.result.reported_by),
          reportedAt: date(i.result.reported_at),
          link: opt(i.result.link),
          note: opt(i.result.note),
        }
      : undefined,
    expiresAt: optDate(i.expires_at),
    check: i.check
      ? { verdict: oneOf<CheckVerdict>(i.check.verdict, verdicts, 'unclear'), reason: i.check.reason, by: i.check.by }
      : undefined,
    createdAt: date(i.created_at),
    updatedAt: date(i.updated_at),
    etag,
  };
}

export const eventFrom = (e: S['ItemEvent']): ItemEvent => ({
  id: e.id,
  itemId: e.item_id,
  itemTitle: e.item_title,
  workspaceId: e.workspace_id,
  kind: (eventKinds as Record<string, EventKind>)[e.type] ?? 'unknown',
  actor: actorFrom(e.actor),
  createdAt: date(e.created_at),
  employee: e.employee ? employeeSummaryFrom(e.employee) : undefined,
  data: (e.data ?? {}) as Record<string, unknown>,
});

// ---- Payloads ----

type Raw = Record<string, unknown>;

/** A non-empty string, or nothing. */
const s = (v: unknown) => (typeof v === 'string' && v.length > 0 ? v : undefined);
const text = (v: unknown) => (typeof v === 'string' ? v : '');
const strings = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const map = (v: unknown): Raw => (typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Raw) : {});
const maps = (v: unknown): Raw[] => (Array.isArray(v) ? v.filter((x) => typeof x === 'object' && x !== null) : []);
const contact = (v: unknown): Contact => {
  const m = map(v);
  return { name: s(m.name), email: s(m.email), company: s(m.company), phone: s(m.phone) };
};
const metrics = (v: unknown): Metric[] =>
  maps(v).map((m): Metric => ({ label: String(m.label), value: String(m.value), delta: s(m.delta) }));
const finite = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
const adPlatforms = ['facebook', 'instagram', 'google', 'tiktok', 'linkedin', 'other'] as const;
const budgetPeriods = ['day', 'month', 'total'] as const;
/** A budget with at least the new amount, or nothing. */
function budget(v: unknown): AdBudget | undefined {
  const b = map(v);
  const to = finite(b.to);
  if (to === undefined) return undefined;
  return {
    from: finite(b.from),
    to,
    currency: s(b.currency) ?? 'EUR',
    period: oneOf<AdBudget['period']>(text(b.period), budgetPeriods, 'total'),
  };
}
const parsedDate = (v: unknown) => {
  const d = typeof v === 'string' ? new Date(v) : undefined;
  return d && !Number.isNaN(d.getTime()) ? d : undefined;
};

export function payloadFrom(schema: string, raw: Raw): ItemPayload {
  switch (schema) {
    case 'social_post.v1':
      return {
        type: 'social_post',
        raw,
        channel: s(raw.channel) ?? 'other',
        text: text(raw.text),
        language: s(raw.language),
        target: s(raw.target),
        targetUrl: s(raw.target_url),
        publishAt: parsedDate(s(raw.publish_at)),
        headline: s(raw.headline),
        link: s(raw.link),
        firstComment: s(raw.first_comment),
        notes: s(raw.notes),
        media: strings(raw.media),
      };
    case 'message_reply.v1':
      return {
        type: 'message_reply',
        raw,
        channel: s(raw.channel) ?? '',
        to: contact(raw.to),
        text: text(raw.text),
        subject: s(raw.subject),
        inReplyTo: s(raw.in_reply_to),
        notes: s(raw.notes),
      };
    case 'offer.v1':
      return {
        type: 'offer',
        raw,
        client: contact(raw.client),
        title: text(raw.title),
        summary: text(raw.summary),
        currency: s(raw.currency) ?? 'EUR',
        prices: maps(raw.prices).map(
          (p): PriceLine => ({
            key: String(p.key),
            label: String(p.label),
            amount: typeof p.amount === 'number' ? p.amount : 0,
            monthly: p.recurring === 'monthly',
            optional: p.optional === true,
          }),
        ),
        scope: strings(raw.scope),
        outOfScope: strings(raw.out_of_scope),
        acceptanceCriteria: strings(raw.acceptance_criteria),
        message: typeof raw.message === 'string' ? raw.message : undefined,
        notes: s(raw.notes),
      };
    case 'deliverable.v1':
      return {
        type: 'deliverable',
        raw,
        title: text(raw.title),
        url: text(raw.url),
        summary: s(raw.summary),
        checklist: maps(raw.checklist).flatMap((c): ChecklistEntry[] =>
          s(c.text) ? [{ text: c.text as string, done: c.done === true }] : [],
        ),
        client: contact(raw.client),
        message: typeof raw.message === 'string' ? raw.message : undefined,
        notes: s(raw.notes),
      };
    case 'task.v1': {
      const source = map(raw.source);
      const sourceSchema = s(source.schema) ?? '';
      return {
        type: 'task',
        raw,
        note: s(raw.note),
        source: {
          itemId: String(source.item_id ?? ''),
          type: s(source.type) ?? '',
          schema: sourceSchema,
          title: text(source.title),
          summary: s(source.summary),
          externalRef: s(source.external_ref),
          employee: s(source.employee),
          // The original work renders as it did on its own page.
          payload:
            typeof source.payload === 'object' && source.payload !== null
              ? payloadFrom(sourceSchema, map(source.payload))
              : { type: 'hidden' },
        },
      };
    }
    case 'access_request.v1':
      return {
        type: 'access_request',
        raw,
        resource: text(raw.resource),
        purpose: text(raw.purpose),
        target: text(raw.target),
        kind: s(raw.kind),
        requestedBy: s(raw.requested_by),
        validMinutes: typeof raw.valid_minutes === 'number' ? Math.trunc(raw.valid_minutes) : undefined,
        notes: s(raw.notes),
      };
    case 'inbound_message.v1':
      return {
        type: 'inbound_message',
        raw,
        channel: s(raw.channel) ?? '',
        from: contact(raw.from),
        text: text(raw.text),
        subject: s(raw.subject),
        receivedAt: parsedDate(raw.received_at),
        context: map(raw.context),
      };
    case 'report.v1':
      return {
        type: 'report',
        raw,
        text: text(raw.text),
        period: s(raw.period),
        metrics: metrics(raw.metrics),
        suggestHandoff: raw.suggest_handoff === true,
      };
    case 'ad_change.v1':
      return {
        type: 'ad_change',
        raw,
        platform: oneOf<AdPlatform>(text(raw.platform), adPlatforms, 'other'),
        campaign: text(raw.campaign),
        change: text(raw.change),
        reason: s(raw.reason),
        budget: budget(raw.budget),
        adText: s(raw.ad_text),
        expected: s(raw.expected),
        metrics: metrics(raw.metrics),
      };
    case 'document.v1':
      return { type: 'document', raw, title: text(raw.title), body: text(raw.body), url: s(raw.url), for: contact(raw.for) };
  }
  return { type: 'unknown', raw };
}
